import React, { useState, useMemo } from 'react';
import { Table, Button, Form, Pagination, Row, Col } from 'react-bootstrap';
import { Predio, PrediosPrograma, UserByDistance } from '../../../../../../service/types/types';
import { PlusCircle, PlusCircleFill, XCircleFill } from 'react-bootstrap-icons';
import UsuariosCercanosProgramaModal from '../../../predioPrograma/modals/closestUsersPrograma.modal';

type PredioTableProps = {
  predios: PrediosPrograma[];
  rowsPerPage?: number;
  onSelectPredio: (predioId: Predio) => void;
  onAddUser: (predio: PrediosPrograma, user: UserByDistance, turno: string) => void;
  onRemoveUser: (predio: PrediosPrograma, user: UserByDistance, turno: string) => void;
  trabajadorPredio: Map<string,string>;
};

export default function PredioProgramaTurno({ predios, onSelectPredio, rowsPerPage = 10, onAddUser, onRemoveUser, trabajadorPredio }: PredioTableProps) {
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  // Filtrar predios según búsqueda
  const filteredPredios = useMemo(() => {
    return predios.filter((p) =>
      [p.name].some((field) =>
        field?.toLowerCase().includes(search.toLowerCase())
      )
    );
  }, [predios, search]);
  // Calcular paginación
  const totalPages = Math.ceil(filteredPredios.length / rowsPerPage);
  const paginatedPredios = filteredPredios.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  const getPageNumbers = (totalPages: number, currentPage: number, delta = 2) => {
    const range: (number | string)[] = [];
    const rangeWithDots: (number | string)[] = [];
    let l: number | undefined; // 👈 allow undefined initially

    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || (i >= currentPage - delta && i <= currentPage + delta)) {
        range.push(i);
      }
    }

    for (let i of range) {
      if (l) {
        if (i as number - l === 2) {
          rangeWithDots.push(l + 1);
        } else if (i as number - l !== 1) {
          rangeWithDots.push('...');
        }
      }
      rangeWithDots.push(i);
      l = i as number;
    }
    return rangeWithDots;
  };

  return (
    <div>
      {/* 🔎 Buscador */}
      <Form.Control
        type="text"
        placeholder= {"Buscar Predio Seleccionado"}
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
          setCurrentPage(1); // resetear a primera página al buscar
        }}
        className="mb-3"
      />

      <Table striped bordered hover responsive>
        <thead>
          <tr>
            <th>Nombre</th>
            <th style={{textAlign: 'center'}}>Mañana</th>
            <th style={{textAlign: 'center'}}>Tarde</th>
            <th style={{textAlign: 'center'}}>Noche</th>
            <th style={{textAlign: 'center'}}>Eliminar</th>
          </tr>
        </thead>
        <tbody>
          {paginatedPredios.map((predio) => (
            <tr key={predio.id}>
              <td>{predio.name}</td>
              <td>
                <Row>
                  <Col className="d-flex align-items-center">{predio.usersM?.length ? predio.usersM?.length : 0}</Col>
                  <Col className="d-flex justify-content-end">
                    <UsuariosCercanosProgramaModal 
                      predio={predio} 
                      onAddUser={onAddUser} 
                      onRemoveUser={onRemoveUser}
                      trabajadorPredio={trabajadorPredio}
                      turno='M'
                    />
                  </Col>
                </Row>
              </td>
              <td>
                <Row>
                  <Col className="d-flex align-items-center">{predio.usersT?.length ? predio.usersT?.length : 0}</Col>
                  <Col className="d-flex justify-content-end">
                    <UsuariosCercanosProgramaModal 
                      predio={predio} 
                      onAddUser={onAddUser} 
                      onRemoveUser={onRemoveUser}
                      trabajadorPredio={trabajadorPredio}
                      turno='T'
                    />
                  </Col>
                </Row>
              </td>
              <td>
                <Row>
                  <Col className="d-flex align-items-center">{predio.usersN?.length ? predio.usersN?.length : 0}</Col>
                  <Col className="d-flex justify-content-end">
                    <UsuariosCercanosProgramaModal 
                      predio={predio} 
                      onAddUser={onAddUser} 
                      onRemoveUser={onRemoveUser}
                      trabajadorPredio={trabajadorPredio}
                      turno='N'
                    />
                  </Col>
                </Row>
              </td>
              <td className="d-flex justify-content-center">
                <div >
                  <Button  variant={'danger'} size="sm" className="me-2 " onClick={() => onSelectPredio(predio)}>
                    <div><XCircleFill className="me-1" />Eliminar</div>
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      {/* 📄 Paginación */}
      <Pagination>
        <Pagination.Prev disabled={currentPage === 1} onClick={() => setCurrentPage((p) => p - 1)}/>
        {getPageNumbers(totalPages, currentPage).map((page, idx) =>
          page === '...' ? (<Pagination.Ellipsis key={idx} disabled />) : (
            <Pagination.Item
              key={`page-${page}-${idx}`} 
              active={page === currentPage}
              onClick={() => setCurrentPage(page as number)}
            >
              {page}
            </Pagination.Item>
          )
        )}
        <Pagination.Next disabled={currentPage === totalPages} onClick={() => setCurrentPage((p) => p + 1)} />
      </Pagination>
    </div>
  );
}